import { useEffect, useState } from 'react';

import CommonImage from '@/components/CommonImage';
import ModalContainer from '@/components/modals/ModalContainer';
import SingleListItem from '@/features/assets/components/CommonComponents/SingleListItem';
import LoaderComponent from '@/components/LoaderComponent';
import { handleCopyToClipboard, onGetLocalFromUtc } from '@/utils/Util';
import { capitalizeFirstLetter } from '@/features/trend-research/utils/utils';
import { getArtworkStatus, getDocType, sliceText } from '@/features/assets/utils';
import CustomTooltip, { POSITIONS } from '@/components/CustomTooltip';

const ArtworkViewModal = ({
	selectedFileInfo,
	onClose,
	onOpenPdfView,
	setSelectedArtworkId,
	isPSDFile,
	artworkDetails,
	isArtworkDetailsLoading
}) => {
	const [imageResponse, setImageResponse] = useState(null);
	const [fileResponse, setFileResponse] = useState(null);

	useEffect(() => {
		if (selectedFileInfo?.documentResponseList?.length > 0) {
			selectedFileInfo?.documentResponseList?.forEach((item) => {
				if (item?.docType === 'ARTWORK_FRONT_IMAGE') {
					setImageResponse(item);
				} else if (getDocType(item?.docType)) {
					setFileResponse(item);
				}
			});
		}
	}, [selectedFileInfo?.id]);

	return (
		<LoaderComponent loading={isArtworkDetailsLoading}>
			<ModalContainer className="category-pom-modal" onCloseModal={() => {}}>
				<div className="common-popup category-pom-modal">
					<div className="common-popup-header d-flex justify-content-between">
						<div className="popup-title d-flex align-items-center">
							<h3 className="semibold-16 mb-0">Artwork View</h3>
						</div>
						<div className="close-btn" onClick={onClose}>
							<CommonImage src={`/icons/close.svg`} alt="close" />
						</div>
					</div>

					<div className="common-popup-body product-slider-body scroll-y-label scollable-modal-body shadow-1dp">
						<div className="product-slider-section assets-slide-section h-auto">
							<div className="product-information d-flex justify-content-between align-items-start gap-4">
								<div className="product-list d-flex">
									<div className="slider-nav d-flex flex-column justify-content-start"></div>
									<div className="pom-product-slider p-3">
										<div className="product-single text-center">
											<CommonImage
												className="w-100 h-100"
												src={imageResponse?.docUrl}
												alt="product"
												isStatic={false}
											/>
										</div>
									</div>
								</div>
								<div className="slider-info">
									<div className="product-description">
										<ul>
											{artworkDetails?.name && (
												<SingleListItem
													title="Artwork Name"
													value={artworkDetails?.name}
												/>
											)}
											{artworkDetails?.seasonTagResponse?.text && (
												<SingleListItem
													title="Season"
													value={artworkDetails?.seasonTagResponse?.text}
												/>
											)}

											{artworkDetails?.artworkThemeTagResponse?.text && (
												<SingleListItem
													title="Theme"
													value={artworkDetails?.artworkThemeTagResponse?.text}
												/>
											)}
											{artworkDetails?.brandResponse?.name && (
												<SingleListItem
													title="Brand"
													value={artworkDetails?.brandResponse?.name}
												/>
											)}

											{artworkDetails?.designedByResponse?.name && (
												<SingleListItem
													title="Design By"
													value={artworkDetails?.designedByResponse?.name}
												/>
											)}

											{artworkDetails?.createdByResponse?.name && (
												<SingleListItem
													title="Created By"
													value={artworkDetails?.createdByResponse?.name}
												/>
											)}
											{artworkDetails?.createdAt && (
												<SingleListItem
													title="Created Date"
													value={onGetLocalFromUtc(
														artworkDetails?.createdAt,
														'DD MMM, YYYY'
													)}
												/>
											)}

											{artworkDetails?.tagResponseList?.length > 0 && (
												<SingleListItem
													title="Tag"
													isTagList
													tagList={artworkDetails?.tagResponseList || []}
												/>
											)}

											{artworkDetails?.remarks && (
												<SingleListItem
													title="Comment"
													value={artworkDetails?.remarks}
												/>
											)}
										</ul>
										<div className="mt-4">
											<span
												className={`${!isPSDFile() ? 'cursor-pointer' : ''} chip-square py-1`}
												onClick={() => {
													if (isPSDFile()) {
														return;
													}
													onOpenPdfView();
													setSelectedArtworkId(null);
												}}
											>
												<CustomTooltip
													text={fileResponse?.name}
													position={POSITIONS.TOP}
													arrowCenter={true}
												>
													{sliceText(fileResponse?.name, 10)}
												</CustomTooltip>
											</span>
											<CommonImage
												onClick={() => window.open(fileResponse?.docUrl, '_blank')}
												src={`/icons/download-gray-dark-02.svg`}
												alt="download"
												width="20px"
												height="20px"
												className="cursor-pointer ml-2"
											/>
										</div>
									</div>
								</div>
							</div>

							<div className="artboard-table pt-4 pb-2">
								<table className="table with-border height-sm hover">
									<thead>
										<tr>
											<th>
												<div className="d-flex gap-2 align-items-center">
													<p>Artboard No </p>
													<p className="regular-14 gray_dark_02">
														({artworkDetails?.artworkArtboardResponseList?.length}{' '}
														Artboards)
													</p>
												</div>
											</th>
											<th>Status</th>
											<th>Style Number</th>
										</tr>
									</thead>
									<tbody>
										{artworkDetails?.artworkArtboardResponseList?.map((item) => (
											<tr key={item?.id}>
												<td className="artboard-column-no">
													<div className="d-flex gap-4 align-items-center">
														<p>{item?.artboardReferenceNumber}</p>
														<div
															className="cursor-pointer"
															onClick={() =>
																handleCopyToClipboard(
																	item?.artboardReferenceNumber,
																	`Artboard No ${item?.artboardReferenceNumber} copied`
																)
															}
														>
															<CommonImage
																src={`/icons/copy-gray.svg`}
																alt="copy"
																width={16}
																height={16}
															/>
														</div>
													</div>
												</td>
												<td>
													<span
														className={`status ${getArtworkStatus(item?.status)}`}
													>
														{capitalizeFirstLetter(item?.status)}
													</span>
												</td>
												<td>
													{item?.artworkProductResponseList?.map((product) => (
														<p>{product?.productReferenceNumber}</p>
													))}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</ModalContainer>
		</LoaderComponent>
	);
};

export default ArtworkViewModal;
